import { useState } from 'react';
import { LINE_COLORS, randomColor, colorForSubject, nextSchoolDay } from '../lib/underground';
import { createAssignment, updateAssignment, replaceSteps } from '../lib/supabase';

const SUBJECTS = ['Math', 'Literacy', 'Social Studies', 'Science', 'French', 'P.E.', 'Elective'];
const STEP_PRESETS = ['Contact Teacher', 'Read', 'Annotate', 'Write', 'Do', 'Check', 'Submit'];

export default function AssignmentModal({ studentId, assignment, userId, isStudent, studentDbId, onSaved, onClose }) {
  const editing = !!assignment;
  const initialSteps = editing && assignment.steps
    ? [...assignment.steps].sort((a, b) => a.position - b.position).map(s => ({ label: s.label, done: s.done, id: s.id }))
    : [{ label: '', done: false }];

  const [name, setName] = useState(editing ? assignment.name : '');
  const [subject, setSubject] = useState(editing ? assignment.subject || '' : '');
  const [customSubject, setCustomSubject] = useState(!SUBJECTS.includes(assignment?.subject || '') && !!assignment?.subject);
  const [dueDate, setDueDate] = useState(editing ? assignment.due_date : nextSchoolDay());
  const [color, setColor] = useState(editing ? assignment.color : randomColor());
  const [steps, setSteps] = useState(initialSteps);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function updateStep(i, val) {
    setSteps(prev => prev.map((s, idx) => idx === i ? { ...s, label: val } : s));
  }
  function addStep() { setSteps(prev => [...prev, { label: '', done: false }]); }
  function addPresetStep(preset) {
    // If already in steps, remove it (toggle off)
    if (steps.some(s => s.label === preset)) {
      const remaining = steps.filter(s => s.label !== preset);
      // Keep at least one empty step so editor doesn't disappear
      setSteps(remaining.length > 0 ? remaining : [{ label: '', done: false }]);
      return;
    }
    setSteps(prev => [...prev.filter(s => s.label.trim()), { label: preset, done: false }]);
  }
  function removeStep(i) { const remaining = steps.filter((_, idx) => idx !== i); setSteps(remaining.length > 0 ? remaining : [{ label: '', done: false }]); }

  async function handleSave() {
    if (!name.trim()) { setError('Please enter an assignment name.'); return; }
    if (!subject.trim()) { setError('Please select a subject.'); return; }
    if (!dueDate) { setError('Please choose a due date.'); return; }
    const cleanSteps = steps.filter(s => s.label.trim());
    if (!cleanSteps.length) { setError('Add at least one step.'); return; }
    setSaving(true);
    setError('');
    try {
      const addedBy = isStudent ? studentDbId : userId;
      if (editing) {
        await updateAssignment(assignment.id, { name: name.trim(), subject: subject.trim(), due_date: dueDate, color });
        await replaceSteps(assignment.id, cleanSteps);
      } else {
        await createAssignment(studentId, { name: name.trim(), subject: subject.trim(), due_date: dueDate, color }, cleanSteps, addedBy, isStudent);
      }
      onSaved();
    } catch (e) {
      setError('Something went wrong — please try again.');
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h2>{editing ? 'Edit Assignment' : 'New Assignment'}</h2>

        <div className="form-group">
          <label>Assignment Name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Fractions worksheet" />
        </div>

        <div className="form-group">
          <label>Subject</label>
          {!customSubject ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                value={subject}
                onChange={e => {
                  const s = e.target.value;
                  setSubject(s);
                  const auto = colorForSubject(s);
                  if (auto) setColor(auto);
                }}
                style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 15 }}
              >
                <option value="">Select a subject…</option>
                {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <button className="btn btn-ghost btn-sm" onClick={() => setCustomSubject(true)} style={{ whiteSpace: 'nowrap' }}>
                + Custom
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Type subject…" />
              <button className="btn btn-ghost btn-sm" onClick={() => { setCustomSubject(false); setSubject(''); }} style={{ whiteSpace: 'nowrap' }}>
                Use list
              </button>
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Due Date</label>
          <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
        </div>

        {!colorForSubject(subject) && (
          <div className="form-group">
            <label>Line Colour</label>
            <div className="color-picker">
              {LINE_COLORS.map(c => (
                <div key={c.hex}
                  className={`color-swatch ${c.hex === color ? 'selected' : ''}`}
                  style={{ background: c.hex }}
                  title={c.name}
                  onClick={() => setColor(c.hex)}
                />
              ))}
            </div>
          </div>
        )}
        {colorForSubject(subject) && (
          <div className="form-group">
            <label>Line Colour</label>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 24, height: 24, borderRadius: "50%", background: color }} />
              <span style={{ fontSize: 13, color: "var(--muted)" }}>
                Auto-assigned from subject
              </span>
            </div>
          </div>
        )}

        <div className="form-group">
          <label>Steps to Complete</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            {STEP_PRESETS.map(p => {
              const already = steps.some(s => s.label === p);
              return (
                <button key={p} onClick={() => addPresetStep(p)} style={{
                  fontSize: 12, padding: '4px 10px', borderRadius: 10,
                  border: '1.5px solid var(--blue)',
                  background: already ? 'var(--blue)' : 'transparent',
                  color: already ? 'white' : 'var(--blue)',
                  cursor: already ? 'default' : 'pointer',
                  fontWeight: 600
                }}>
                  {already ? '✓ ' : '+ '}{p}
                </button>
              );
            })}
          </div>
          <div className="steps-editor">
            {steps.map((s, i) => (
              <div key={i} className="step-row">
                <input value={s.label} onChange={e => updateStep(i, e.target.value)} placeholder={`Step ${i + 1}`} />
                <button onClick={() => removeStep(i)} title="Remove step" style={{ opacity: steps.length === 1 ? 0.3 : 1 }}>✕</button>
              </div>
            ))}
          </div>
          <button className="add-step-btn" onClick={addStep}>+ Add a custom step</button>
        </div>

        {error && (
          <div style={{ background: '#fee', color: '#c00', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 8 }}>
            {error}
          </div>
        )}

        <div className="modal-btns">
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
